/**
 * BookingDetailPage Component
 *
 * Detailed view of a single booking with full information and actions.
 */

import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useHostBookings, useBookingActions, type BookingStatus } from './hooks';
import { AcceptModal, DeclineModal, CancelModal } from './components';
import { HostLayout } from '../components';
import { usePlatformCommissionRate } from '@/services/platformFees';

const STATUS_CONFIG: Record<BookingStatus, { label: string; color: string; bg: string }> = {
  pending: { label: 'Pending Approval', color: 'text-amber-700', bg: 'bg-amber-100' },
  confirmed: { label: 'Confirmed', color: 'text-green-700', bg: 'bg-green-100' },
  active: { label: 'Guest Checked In', color: 'text-blue-700', bg: 'bg-blue-100' },
  completed: { label: 'Completed', color: 'text-gray-700', bg: 'bg-gray-100' },
  cancelled: { label: 'Cancelled', color: 'text-red-700', bg: 'bg-red-100' },
  declined: { label: 'Declined', color: 'text-gray-500', bg: 'bg-gray-100' },
};

export const BookingDetailPage: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();

  const { bookings, isLoading, error, refetch } = useHostBookings();
  const commissionPercent = usePlatformCommissionRate();
  const {
    acceptBooking,
    declineBooking,
    cancelBooking,
    checkInGuest,
    checkOutGuest,
    isProcessing,
  } = useBookingActions();

  // Modal state
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const booking = bookings.find((b) => b.id === bookingId);

  if (isLoading) {
    return (
      <HostLayout title="Booking Details">
        <div className="max-w-4xl animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-64 bg-gray-200 rounded-xl" />
          <div className="h-48 bg-gray-200 rounded-xl" />
        </div>
      </HostLayout>
    );
  }

  if (error) {
    return (
      <HostLayout title="Booking Details">
        <div className="max-w-4xl">
          <div className="bg-red-50 text-red-600 p-4 rounded-xl">
            Error loading booking: {error?.message || 'Unknown error'}
          </div>
        </div>
      </HostLayout>
    );
  }

  if (!booking) {
    return (
      <HostLayout title="Booking Details">
        <div className="max-w-4xl text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Booking not found</h2>
          <Link to="/host/bookings" className="text-primary-600 hover:text-primary-700">
            Back to bookings
          </Link>
        </div>
      </HostLayout>
    );
  }

  const status = STATUS_CONFIG[booking.status];
  const nights = Math.ceil(
    (booking.checkOut.getTime() - booking.checkIn.getTime()) / (1000 * 60 * 60 * 24)
  );

  const isPending = booking.status === 'pending';
  const isConfirmed = booking.status === 'confirmed';
  const isActive = booking.status === 'active';
  const canCancel = isConfirmed && booking.checkIn > new Date();
  const canCheckIn = isConfirmed && booking.checkIn <= new Date() && booking.checkOut > new Date();
  const canCheckOut = isActive;

  const handleAccept = async () => {
    const success = await acceptBooking(booking.id);
    if (success) {
      refetch();
      setShowAcceptModal(false);
    }
  };

  const handleDecline = async (reason: string, message?: string) => {
    const success = await declineBooking({
      bookingId: booking.id,
      reason,
      message,
    });
    if (success) {
      refetch();
      setShowDeclineModal(false);
    }
  };

  const handleCancel = async (reason: string) => {
    const success = await cancelBooking({
      bookingId: booking.id,
      reason,
    });
    if (success) {
      refetch();
      setShowCancelModal(false);
    }
  };

  const handleCheckIn = async () => {
    const success = await checkInGuest(booking.id);
    if (success) {
      refetch();
    }
  };

  const handleCheckOut = async () => {
    const success = await checkOutGuest(booking.id);
    if (success) {
      refetch();
    }
  };

  return (
    <HostLayout>
      <div className="max-w-4xl">
      {/* Back Link */}
      <Link
        to="/host/bookings"
        className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeftIcon className="w-5 h-5 mr-2" />
        Back to bookings
      </Link>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-gray-900">Booking Details</h1>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${status.bg} ${status.color}`}>
              {status.label}
            </span>
          </div>
          <p className="text-gray-500">Booking ID: {booking.id}</p>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2">
          {isPending && (
            <>
              <button
                onClick={() => setShowDeclineModal(true)}
                className="px-4 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50"
              >
                Decline
              </button>
              <button
                onClick={() => setShowAcceptModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700"
              >
                Accept
              </button>
            </>
          )}
          {canCheckIn && (
            <button
              onClick={handleCheckIn}
              disabled={isProcessing}
              className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50"
            >
              {isProcessing ? 'Processing...' : 'Check In Guest'}
            </button>
          )}
          {canCheckOut && (
            <button
              onClick={handleCheckOut}
              disabled={isProcessing}
              className="px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50"
            >
              {isProcessing ? 'Processing...' : 'Check Out Guest'}
            </button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4 lg:space-y-6">
          {/* Listing Card */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="flex">
              <div className="w-20 h-20 sm:w-32 sm:h-32 flex-shrink-0">
                <img
                  src={booking.listingImage}
                  alt={booking.listingTitle}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4 flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">{booking.listingTitle}</h3>
                <p className="text-sm text-gray-500 mb-2">
                  {booking.checkIn.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                  {' → '}
                  {booking.checkOut.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </p>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span>{nights} {nights === 1 ? 'night' : 'nights'}</span>
                  <span>·</span>
                  <span>{booking.guestCount} {booking.guestCount === 1 ? 'guest' : 'guests'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Guest Information */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Guest Information</h3>
            <div className="flex items-start gap-4">
              <img
                src={booking.guestAvatar}
                alt={booking.guestName}
                className="w-16 h-16 rounded-full object-cover"
              />
              <div className="flex-1">
                <p className="font-medium text-gray-900 text-lg">{booking.guestName}</p>
                <p className="text-sm text-gray-500 mb-3">Member since 2023</p>
                <div className="flex flex-wrap gap-4">
                  <Link
                    to={`/host/messages/${booking.id}`}
                    className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700"
                  >
                    <MessageIcon className="w-5 h-5" />
                    Send message
                  </Link>
                  <a
                    href={`tel:+1234567890`}
                    className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
                  >
                    <PhoneIcon className="w-5 h-5" />
                    Call guest
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Booking Timeline</h3>
            <div className="space-y-4">
              <TimelineItem
                icon={<ClockIcon className="w-5 h-5" />}
                title="Booking requested"
                date={new Date(booking.checkIn.getTime() - 7 * 24 * 60 * 60 * 1000)}
                status="completed"
              />
              {(isConfirmed || isActive || booking.status === 'completed') && (
                <TimelineItem
                  icon={<CheckIcon className="w-5 h-5" />}
                  title="Booking confirmed"
                  date={new Date(booking.checkIn.getTime() - 6 * 24 * 60 * 60 * 1000)}
                  status="completed"
                />
              )}
              <TimelineItem
                icon={<CalendarIcon className="w-5 h-5" />}
                title="Check-in"
                date={booking.checkIn}
                status={
                  isActive || booking.status === 'completed'
                    ? 'completed'
                    : booking.checkIn <= new Date()
                    ? 'current'
                    : 'upcoming'
                }
              />
              <TimelineItem
                icon={<CalendarIcon className="w-5 h-5" />}
                title="Check-out"
                date={booking.checkOut}
                status={
                  booking.status === 'completed'
                    ? 'completed'
                    : booking.checkOut <= new Date()
                    ? 'current'
                    : 'upcoming'
                }
              />
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Payment Summary */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Payment Summary</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">
                  Guest pays (total)
                </span>
                <span className="text-gray-900">${booking.totalPrice}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">
                  Platform commission ({commissionPercent}%)
                </span>
                <span className="text-gray-900">
                  -${booking.platformCommission || Math.round(booking.totalPrice * (commissionPercent / 100))}
                </span>
              </div>
              <div className="flex justify-between pt-3 border-t font-medium">
                <span className="text-gray-900">
                  Your payout ({100 - commissionPercent}%)
                </span>
                <span className="text-green-600 text-lg">${booking.hostPayout}</span>
              </div>
            </div>
            {(isConfirmed || isActive) && (
              <p className="text-xs text-gray-500 mt-4">
                Payout will be available after guest checks out. You can then request a withdrawal.
              </p>
            )}
          </div>

          {/* Actions */}
          {canCancel && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Actions</h3>
              <button
                onClick={() => setShowCancelModal(true)}
                className="w-full py-3 border border-red-300 text-red-600 rounded-xl hover:bg-red-50"
              >
                Cancel Booking
              </button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Cancellation may result in penalties
              </p>
            </div>
          )}

          {/* Need Help */}
          <div className="bg-gray-50 rounded-2xl p-6">
            <h3 className="font-semibold text-gray-900 mb-2">Need help?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Contact our support team for assistance with this booking.
            </p>
            <button className="w-full py-2 text-primary-600 hover:text-primary-700 font-medium text-sm">
              Contact Support
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AcceptModal
        isOpen={showAcceptModal}
        onClose={() => setShowAcceptModal(false)}
        onAccept={handleAccept}
        isLoading={isProcessing}
        booking={{
          guestName: booking.guestName,
          listingTitle: booking.listingTitle,
          checkIn: booking.checkIn,
          checkOut: booking.checkOut,
          nights,
          hostPayout: booking.hostPayout,
        }}
      />

      <DeclineModal
        isOpen={showDeclineModal}
        onClose={() => setShowDeclineModal(false)}
        onDecline={handleDecline}
        isLoading={isProcessing}
        guestName={booking.guestName}
      />

      <CancelModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onCancel={handleCancel}
        isLoading={isProcessing}
        booking={{
          guestName: booking.guestName,
          checkIn: booking.checkIn,
          checkOut: booking.checkOut,
          hostPayout: booking.hostPayout,
        }}
      />
      </div>
    </HostLayout>
  );
};

// Timeline Item Component
interface TimelineItemProps {
  icon: React.ReactNode;
  title: string;
  date: Date;
  status: 'completed' | 'current' | 'upcoming';
}

const TimelineItem: React.FC<TimelineItemProps> = ({ icon, title, date, status }) => {
  const statusStyles = {
    completed: 'bg-green-100 text-green-600',
    current: 'bg-blue-100 text-blue-600',
    upcoming: 'bg-gray-100 text-gray-400',
  };

  return (
    <div className="flex items-center gap-4">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${statusStyles[status]}`}>
        {icon}
      </div>
      <div className="flex-1">
        <p className={`font-medium ${status === 'upcoming' ? 'text-gray-400' : 'text-gray-900'}`}>
          {title}
        </p>
        <p className="text-sm text-gray-500">
          {date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </p>
      </div>
      {status === 'completed' && (
        <CheckCircleIcon className="w-5 h-5 text-green-500" />
      )}
    </div>
  );
};

// Icons
const ArrowLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const MessageIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
    />
  </svg>
);

const PhoneIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
    />
  </svg>
);

const ClockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const CheckCircleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path
      fillRule="evenodd"
      d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
      clipRule="evenodd"
    />
  </svg>
);

export default BookingDetailPage;
